import type { CSSProperties } from 'react'

import { VcnCardInUseStatus, VcnCardStatus } from './types'
import { statusPill } from './tokens'

export const VCN_CARD_STATUS_LABELS: Record<VcnCardStatus, string> = {
  [VcnCardStatus.ACTIVE]: 'Active',
  [VcnCardStatus.SUSPENDED]: 'Suspended',
  [VcnCardStatus.TERMINATED]: 'Terminated',
}

export function getVcnCardStatusLabel(status: VcnCardStatus): string {
  return VCN_CARD_STATUS_LABELS[status]
}

/**
 * SureCo design-system `.status` pill (from the EMS-8827 design system export):
 * a full-capsule badge, Inter 12/16 medium, 2px 10px, with semantic bg/border/
 * text per tone. Hardcoded hex — the admin app has no design-token CSS vars.
 *
 * These tones stay as the IN-USE vocabulary. Card status has its own palette
 * from P'Sing's VCN Table design — see {@link getVcnCardStatusPillStyle}. The
 * two columns sit side by side and mean different things, so they are allowed
 * to disagree about what green is.
 */
export type StatusTone = 'success' | 'pending' | 'error' | 'info'

const STATUS_TONE_COLORS: Record<
  StatusTone,
  { background: string; borderColor: string; color: string }
> = {
  success: { background: '#F0FDF4', borderColor: '#BBF7D0', color: '#4A6628' },
  pending: { background: '#FEF9E7', borderColor: '#FDE396', color: '#92400E' },
  error: { background: '#FEE2E2', borderColor: '#FCA5A5', color: '#7A2020' },
  info: { background: '#FFFFFF', borderColor: '#D9E2EC', color: '#112A43' },
}

const STATUS_PILL_BASE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'Inter, sans-serif',
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '16px',
  padding: '2px 10px',
  borderRadius: 9999,
  borderWidth: 1,
  borderStyle: 'solid',
}

export function getStatusPillStyle(tone: StatusTone): CSSProperties {
  return { ...STATUS_PILL_BASE, ...STATUS_TONE_COLORS[tone] }
}

/**
 * Card-status pill in the design's own colours. Suspended is absent from the
 * design file, so it keeps the shared amber rather than being invented.
 */
export function getVcnCardStatusPillStyle(
  status: VcnCardStatus
): CSSProperties {
  if (status === VcnCardStatus.ACTIVE) {
    return { ...STATUS_PILL_BASE, ...statusPill.active }
  }
  if (status === VcnCardStatus.TERMINATED) {
    return { ...STATUS_PILL_BASE, ...statusPill.terminated }
  }
  return getStatusPillStyle(getVcnCardStatusTone(status))
}

export function getVcnCardStatusTone(status: VcnCardStatus): StatusTone {
  switch (status) {
    case VcnCardStatus.ACTIVE:
      return 'success'
    case VcnCardStatus.SUSPENDED:
      return 'pending'
    case VcnCardStatus.TERMINATED:
      return 'error'
    default:
      return 'info'
  }
}

/**
 * PM wording (2026-09-15 demo): the column reads in terms of the plan a card is
 * on, not whether the card is "in use". The enum values behind these are
 * unchanged — they are wire contract, and nobody sees them.
 */
export const VCN_CARD_IN_USE_LABELS: Record<VcnCardInUseStatus, string> = {
  [VcnCardInUseStatus.IN_USE]: 'On Active Plan',
  [VcnCardInUseStatus.IN_USE_ENDED]: 'On Ended Plan',
  [VcnCardInUseStatus.NOT_IN_USE]: 'No Plan',
}

export function getVcnCardInUseLabel(inUse: VcnCardInUseStatus): string {
  return VCN_CARD_IN_USE_LABELS[inUse]
}

/**
 * Orphan cleanup reads off this column, so the tones are picked for scanning:
 * a card no benefit uses is the row to terminate, and an ended benefit is the
 * one to check before terminating.
 */
export function getVcnCardInUseTone(inUse: VcnCardInUseStatus): StatusTone {
  switch (inUse) {
    case VcnCardInUseStatus.IN_USE:
      return 'success'
    case VcnCardInUseStatus.IN_USE_ENDED:
      return 'pending'
    case VcnCardInUseStatus.NOT_IN_USE:
      return 'error'
    default:
      return 'info'
  }
}
